import { captureException } from "@sentry/react";
import { Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useRouteError,
} from "react-router";

import { RequirePermit } from "~auth/RequirePermit.tsx";
import { Layout } from "~components/App/Layout/Layout.tsx";
import { PageGroup } from "~components/App/Routes/PageGroup/PageGroup.tsx";
import { Dashboard } from "~components/Dashboard/Dashboard.tsx";
import { LazyNotifications } from "~components/lazyLoadedComponents.ts";
import { ROUTES } from "~components/routeDefinitions.ts";
import { GenericError } from "~components/shared/ErrorPages/GenericError.tsx";
import { NotFound } from "~components/shared/ErrorPages/NotFound.tsx";
import { Spinner } from "~components/shared/Loading/Spinner/Spinner.tsx";
import { useSetPostHogProperties } from "~components/shared/postHog/useSetPostHogProperties.ts";
import { OfflineContextProvider } from "~contexts/OfflineContext/OfflineContext.tsx";
import { RequireFeaturesLoaded } from "~lib/featureToggles/RequireFeaturesLoaded.tsx";
import { useNavigationDefinitions } from "../../useNavigationDefinitions.tsx";
import { RequireAppAccess } from "./RedirectAppAccess/RequireAppAccess.tsx";
import { RequireTenantAccess } from "./RedirectTenantAccess/RequireTenantAccess.tsx";
import { RedirectToMainTenant } from "./RedirectToMainTenant/RedirectToMainTenant.tsx";
import { RedirectToTool } from "./RedirectToTool/RedirectToTool.tsx";
import { RequireValidTenantNumber } from "./RequireValidTenantNumber/RequireValidTenantNumber.tsx";
import { TestError } from "./TestError.tsx";

export function Routes() {
  const { navigationDefinitions, hiddenRoutes } = useNavigationDefinitions();
  useSetPostHogProperties();

  const routeDefinition = createRoutesFromElements(
    <Route path={"/"} element={<Outlet />} errorElement={<ErrorElement />}>
      <Route
        path={ROUTES.dashboard.path}
        element={
          <RequireAppAccess>
            <RequireValidTenantNumber>
              <RequireTenantAccess>
                <OfflineContextProvider>
                  <Layout>
                    <Suspense fallback={<Spinner />}>
                      <RequireFeaturesLoaded>
                        <RequirePermit>
                          <Outlet />
                        </RequirePermit>
                      </RequireFeaturesLoaded>
                    </Suspense>
                  </Layout>
                </OfflineContextProvider>
              </RequireTenantAccess>
            </RequireValidTenantNumber>
          </RequireAppAccess>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path={ROUTES.notifications.path}
          element={<LazyNotifications />}
        />

        {navigationDefinitions.map((group) => (
          <Route
            key={group.titleTranslationKey}
            element={
              <PageGroup navigationGroup={group}>
                <Suspense fallback={<Spinner />}>
                  <Outlet />
                </Suspense>
              </PageGroup>
            }
          >
            {group.items.map(({ route, routeProps }) => (
              <Route key={route.path} {...routeProps} />
            ))}
          </Route>
        ))}

        {hiddenRoutes.map((hidden) => {
          const group = "group" in hidden ? hidden.group : undefined;
          const element = group ? (
            <PageGroup navigationGroup={group}>
              {hidden.routeProps.element}
            </PageGroup>
          ) : (
            hidden.routeProps.element
          );
          return (
            <Route
              key={hidden.route.path}
              {...hidden.routeProps}
              element={element}
            />
          );
        })}

        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path={ROUTES.testError.path} element={<TestError />} />
      <Route path={ROUTES.toolDeeplink.path} element={<RedirectToTool />} />
      <Route path="*" element={<RedirectToMainTenant />} />
      <Route index element={<RedirectToMainTenant />} />
    </Route>,
  );

  const router = createBrowserRouter(routeDefinition);

  return <RouterProvider router={router} />;
}

function ErrorElement() {
  const error = useRouteError();

  useEffect(() => {
    captureException(error);
  }, [error]);

  return <GenericError />;
}
